import { waitForMonitorFirstCheck } from "./monitorUtils";
import { useCallback, useMemo, useState, useEffect } from "react";
import { mutate } from "swr";
import { ArrowPathIcon, CheckIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Monitor } from "@/components/models/Monitor";
import useCurrentAccount from "@/components/context/useCurrentAccount";
import useDashboard from "@/components/context/useDashboard";
import { isSubscriptionBlocked } from "@/utils/billing";
import { useRouter } from "next/router";
import { Button } from "@/components/ui/Button";
import { PageHeader } from "@/components/ui/page-header";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/Table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/Input";
import MonitorTableRow from "./MonitorTableRow";

export default function DashboardMonitorsList() {
  const [refreshing, setRefreshing] = useState(false);
  const [refreshCompleted, setRefreshCompleted] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(50);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const { account } = useCurrentAccount({ requireLoggedIn: true });
  const router = useRouter();
  const { monitors, monitors_counts, pagination, isError, isLoading } = useDashboard(
    currentPage,
    perPage,
    debouncedSearch,
  );

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1); // Reset to first page on search
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const onEditMonitor = useCallback(
    (monitor: Monitor) => {
      router.push(`/dashboard/monitors/${monitor.id}/edit`);
    },
    [router],
  );

  // Build query params for the current dashboard view
  const dashboardQueryParams = useMemo(() => {
    const queryParams = new URLSearchParams({
      page: currentPage.toString(),
      per_page: perPage.toString(),
    });
    if (debouncedSearch) {
      queryParams.append("search", debouncedSearch);
    }
    return queryParams.toString();
  }, [currentPage, perPage, debouncedSearch]);

  const refreshMonitors = useCallback(() => {
    if (!refreshing && !refreshCompleted) {
      setRefreshing(true);
      mutate(`/web_api/dashboard?${dashboardQueryParams}`).finally(() => {
        setRefreshCompleted(true);
        setRefreshing(false);

        mutate("/web_api/accounts/current");

        setTimeout(() => setRefreshCompleted(false), 1000);
      });
    }
  }, [refreshCompleted, refreshing, dashboardQueryParams]);

  const deleteMonitor = useCallback(
    (monitorId: string, monitorName: String) => {
      if (!confirm(`Are you sure you want to delete monitor "${monitorName}"?`)) {
        return;
      }

      fetch(`/web_api/monitors/${monitorId}`, {
        method: "DELETE",
        // headers: { 'Content-Type': 'application/json' },
      })
        .then((resp) => resp.json())
        .then(() => {
          // Mutate with the correct cache key that includes query params
          mutate(`/web_api/dashboard?${dashboardQueryParams}`);
          mutate("/web_api/monitors");
          mutate("/web_api/accounts/current");
        })
        .catch((e) => {
          console.error(e);
        });
    },
    [dashboardQueryParams],
  );

  const pauseMonitor = useCallback(
    (monitorId: string) => {
      fetch(`/web_api/monitors/${monitorId}/pause`, {
        method: "POST",
        // headers: { 'Content-Type': 'application/json' },
      })
        .then((resp) => resp.json())
        .then(() => {
          // Mutate with the correct cache key that includes query params
          mutate(`/web_api/dashboard?${dashboardQueryParams}`);
          mutate("/web_api/monitors");
          mutate("/web_api/accounts/current");
        })
        .catch((e) => {
          console.error(e);
        });
    },
    [dashboardQueryParams],
  );

  const unpauseMonitor = useCallback(
    (monitorId: string) => {
      fetch(`/web_api/monitors/${monitorId}/unpause`, {
        method: "POST",
        // headers: { 'Content-Type': 'application/json' },
      })
        .then((resp) => resp.json())
        .then(() => {
          // Mutate with the correct cache key that includes query params
          mutate(`/web_api/dashboard?${dashboardQueryParams}`);
          mutate("/web_api/monitors");

          waitForMonitorFirstCheck(monitorId).then((_monitorAfterFirstCheck) => {
            mutate(`/web_api/accounts/current`);
            mutate(`/web_api/monitors`);
            mutate(`/web_api/dashboard?${dashboardQueryParams}`);
          });
        })
        .catch((e) => {
          console.error(e);
        });
    },
    [dashboardQueryParams],
  );

  const down_count = useMemo(() => {
    return monitors_counts?.down || 0;
  }, [monitors_counts]);

  const newMonitorClicked = useCallback(
    (e: any) => {
      e.preventDefault();

      if (isSubscriptionBlocked(account)) {
        router.push("/dashboard/billing");
        return;
      }

      if (monitors.length >= account?.current_subscription?.monitors) {
        alert(
          "You have reached the limit of monitors for your current subscription. Please upgrade your subscription to add more monitors.",
        );
        return;
      }

      router.push("/dashboard/monitors/new");
    },
    [account, monitors, router],
  );

  const statusTitle = useMemo(() => {
    switch (account?.status) {
      case "all_up":
        return "All systems operational";
      case "all_down":
        return "All systems down";
      case "some_down":
        return "Some systems down";
      default:
        return "Monitors";
    }
  }, [account?.status]);

  const statusDescription = useMemo(() => {
    if (monitors.length === 0) {
      return "Add your first monitor to start tracking uptime";
    }
    if (account?.status === "all_up") {
      return `${monitors.length} ${monitors.length === 1 ? "monitor" : "monitors"} running smoothly`;
    }
    if (down_count > 0) {
      return `${down_count} ${down_count === 1 ? "monitor is" : "monitors are"} currently down`;
    }
    return `Monitoring ${monitors.length} ${monitors.length === 1 ? "service" : "services"}`;
  }, [account?.status, monitors.length, down_count]);

  return (
    <div className="space-y-6">
      <PageHeader title={statusTitle} description={statusDescription}>
        <Button onClick={newMonitorClicked}>
          <PlusIcon className="h-4 w-4 mr-2" />
          New Monitor
        </Button>
      </PageHeader>

      {/* Search and Actions */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search monitors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && monitors.length > 0) {
                router.push(`/dashboard/monitors/${monitors[0].id}`);
              }
            }}
            className="pl-9"
          />
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={refreshMonitors}
          disabled={refreshing || refreshCompleted}
        >
          {refreshCompleted ? (
            <CheckIcon className="h-4 w-4" />
          ) : (
            <ArrowPathIcon className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          )}
          <span className="ml-2">{refreshCompleted ? "Updated" : "Refresh"}</span>
        </Button>
      </div>

      {/* Error State */}
      {isError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 mb-6">
          <p className="text-sm text-destructive">
            Failed to load monitors. Please try refreshing the page.
          </p>
        </div>
      )}

      {/* Monitors Table */}
      <div className="rounded-md border">
        {isLoading ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden md:table-cell">Uptime</TableHead>
                <TableHead className="hidden lg:table-cell">Check Interval</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <MonitorTableRow key={i} isLoading={true} />
              ))}
            </TableBody>
          </Table>
        ) : monitors.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="rounded-full bg-muted p-3 mb-4">
              <ArrowPathIcon className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-1">
              {debouncedSearch ? "No monitors found" : "No monitors yet"}
            </h3>
            <p className="text-muted-foreground text-center max-w-sm">
              {debouncedSearch
                ? `No monitors match "${debouncedSearch}". Try a different search term.`
                : "Get started by adding your first monitor to track uptime and receive alerts."}
            </p>
            {!debouncedSearch && (
              <Button onClick={newMonitorClicked} className="mt-4">
                <PlusIcon className="h-4 w-4 mr-2" />
                Add your first monitor
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden md:table-cell">Uptime</TableHead>
                <TableHead className="hidden lg:table-cell">Check Interval</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {monitors.map((monitor) => (
                <MonitorTableRow
                  key={monitor.id}
                  monitor={monitor}
                  onClick={(m) => router.push(`/dashboard/monitors/${m.id}`)}
                  onEdit={onEditMonitor}
                  onPause={pauseMonitor}
                  onUnpause={unpauseMonitor}
                  onDelete={deleteMonitor}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground whitespace-nowrap">
            Showing {(currentPage - 1) * perPage + 1} to{" "}
            {Math.min(currentPage * perPage, pagination.total)} of {pagination.total} monitors
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  className={
                    currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"
                  }
                />
              </PaginationItem>

              {/* Show all pages if total pages <= 7 */}
              {pagination.total_pages <= 7 ? (
                Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((page) => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      onClick={() => setCurrentPage(page)}
                      isActive={currentPage === page}
                      className="cursor-pointer"
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ))
              ) : (
                <>
                  {/* First page */}
                  {currentPage > 3 && (
                    <>
                      <PaginationItem>
                        <PaginationLink
                          onClick={() => setCurrentPage(1)}
                          className="cursor-pointer"
                        >
                          1
                        </PaginationLink>
                      </PaginationItem>
                      {currentPage > 4 && (
                        <PaginationItem>
                          <PaginationEllipsis />
                        </PaginationItem>
                      )}
                    </>
                  )}

                  {/* Pages around current */}
                  {Array.from({ length: 5 }, (_, i) => {
                    const page = currentPage - 2 + i;
                    if (page < 1 || page > pagination.total_pages) return null;
                    return (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => setCurrentPage(page)}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  }).filter(Boolean)}

                  {/* Last page */}
                  {currentPage < pagination.total_pages - 2 && (
                    <>
                      {currentPage < pagination.total_pages - 3 && (
                        <PaginationItem>
                          <PaginationEllipsis />
                        </PaginationItem>
                      )}
                      <PaginationItem>
                        <PaginationLink
                          onClick={() => setCurrentPage(pagination.total_pages)}
                          className="cursor-pointer"
                        >
                          {pagination.total_pages}
                        </PaginationLink>
                      </PaginationItem>
                    </>
                  )}
                </>
              )}

              <PaginationItem>
                <PaginationNext
                  onClick={() => setCurrentPage(Math.min(pagination.total_pages, currentPage + 1))}
                  className={
                    currentPage === pagination.total_pages
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
